import { BodyText, Card } from '@unmind/design-system';
import { Link, useHistory } from 'react-router-dom';
import { FontSizes } from '@unmind/design-system/theme/deprecated';
import RoutePath from 'App/RoutePath';
import { practitionersQuery_practitioners_edges_node as Practitioner } from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import { getProfileSummary, Profession } from 'Book/shared';
import { capitalize } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { parseBeGatewayId } from 'utils/relay';
import matchAvatarPlaceholder from './assets/browse_practitioners.png';

const MatchCardOuter = styled(Card)<{ $orientation: Props['orientation'] }>`
  display: flex;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          flex-direction: column;
          align-items: flex-start;
        `
      : css`
          flex-direction: row;
          align-items: center;
          padding: 16px;
        `}

  box-shadow: 0px 6px 12px -3px rgba(13, 3, 0, 0.05),
    0px 3px 6px -2px rgba(13, 3, 0, 0.05);

  transition:
    transform 200ms ease-in-out,
    transform 200ms ease-in-out;
  &:active {
    transform: scale(0.98);
    opacity: 0.98;
  }

  &:hover {
    transition:
      transform 200ms ease-in-out,
      transform 200ms ease-in-out;
    box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.08);
    transform: scale(1.01);
  }
`;

const MatchName = styled(BodyText).attrs(({ theme }) => ({
  color: theme.colors.text.primary,
  sizes: [FontSizes.fontSize18],
  weight: theme.typography.fontWeights.semiBold,
}))`
  margin-bottom: 4px;
  word-break: break-word;

  a {
    color: inherit;
  }
`;

const MatchBody = styled(BodyText).attrs({
  sizes: [FontSizes.fontSize16],
})<{ $orientation?: Props['orientation'] }>`
  display: -webkit-box;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${({ $orientation }) =>
    $orientation === 'vertical' &&
    css`
      margin-bottom: 16px;
    `}
`;

export const MatchImage = styled.img<{
  src: string;
  $orientation: Props['orientation'];
}>`
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 16px;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          max-height: 200px;
          width: 100%;
          margin-bottom: 16px;
        `
      : css`
          height: 100px;
          width: 100px;
          margin-right: 16px;
        `}
`;

interface Props {
  match: Practitioner;
  orientation: 'horizontal' | 'vertical';
  trackingSource: 'home_match' | 'browse_match';
}

export default ({ match, orientation, trackingSource }: Props) => {
  const { t } = useTranslation('book');
  const history = useHistory();
  const { id, firstName, lastName, photoUrls, details } = match || {};
  const normalisedId = parseBeGatewayId(id);
  const { profileQuestionResponses, type: practitionerType } = details || {};

  const profileSummary = getProfileSummary(
    profileQuestionResponses,
    practitionerType,
  );
  const profession = details.profileInformation?.profession as Profession;
  const professionValue =
    Profession[profession] && !(profession === Profession.other)
      ? t(`practitioner.professions.${profession}`)
      : null;
  const practitionerTypeLabel = professionValue || capitalize(details.type);
  const photoUrl = photoUrls?.[0]?.url ?? matchAvatarPlaceholder;

  return (
    <MatchCardOuter
      $orientation={orientation}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        history.push({
          pathname: `${RoutePath.BookPractitioners}/${normalisedId}`,
          state: { source: trackingSource },
        });
      }}
    >
      <MatchImage src={photoUrl} alt="" $orientation={orientation} />
      <div>
        <MatchName>
          <Link
            to={{
              pathname: `${RoutePath.BookPractitioners}/${normalisedId}`,
              state: { source: trackingSource },
            }}
          >{`${firstName} ${lastName}`}</Link>
        </MatchName>
        <MatchBody $orientation={orientation}>
          {practitionerTypeLabel}
        </MatchBody>
        {profileSummary && orientation === 'vertical' ? (
          <MatchBody>{profileSummary}</MatchBody>
        ) : null}
      </div>
    </MatchCardOuter>
  );
};
